import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { ArrowLeft } from 'lucide-react';
import api, { API_URL, BACKEND_URL } from '../services/api';
import { toast } from 'sonner';
import WorkspaceSelector from './WorkspaceSelector';

const LoginPage = () => {
  const { user, login, register, showWorkspaceSelector, workspaces, selectWorkspace, cancelWorkspaceSelection } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Register form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    fullName: ''
  });

  // Forgot password form state
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: ''
  });

  // Reset password form state
  const [resetPasswordData, setResetPasswordData] = useState({
    resetToken: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Check if we&apos;re on the reset password page - MUST be before any conditional returns
  useEffect(() => {
    if (location.pathname === '/reset-password') {
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      
      if (token) {
        setResetPasswordData(prev => ({ ...prev, resetToken: token }));
        setShowResetPassword(true);
        setShowForgotPassword(false);
      } else {
        toast.error('Invalid reset link. Please request a new password reset.');
      }
    }
  }, [location]);

  // Show workspace selector if needed
  if (showWorkspaceSelector && workspaces.length > 1) {
    return (
      <WorkspaceSelector 
        workspaces={workspaces}
        onSelectWorkspace={selectWorkspace}
        onCreateWorkspace={() => window.location.href = '/pricing'}
        loading={isLoading}
      />
    );
  }

  // Early return MUST be after all hooks
  if (user) {
    return <Navigate to="/" />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    setIsLoading(false);  // Always reset loading state
    
    if (!result.success) {
      // Error handling is done in the login function via toast
      return;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await register(
      registerData.email,
      registerData.password,
      registerData.fullName
    );
    
    if (!result.success) {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post(`${API_URL}/auth/forgot-password`, {
        email: forgotPasswordData.email
      });

      // Show the backend's actual message. For security (anti-enumeration) the backend returns a
      // NEUTRAL message when the email isn't registered, and a confirmed-sent message when it is.
      toast.success(response.data.message || 'If an account exists with this email, a password reset link will be sent');
      
      // If reset token is returned (development mode), show reset form
      if (response.data.reset_token) {
        setResetPasswordData(prev => ({ ...prev, resetToken: response.data.reset_token }));
        setShowResetPassword(true);
        setShowForgotPassword(false);
        toast.info('Development mode: Use the provided reset token below');
      } else {
        setShowForgotPassword(false);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send password reset email';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await api.post(`${API_URL}/auth/reset-password`, {
        reset_token: resetPasswordData.resetToken,
        new_password: resetPasswordData.newPassword
      });

      toast.success('Password has been reset successfully! You can now login with your new password.');
      
      // Reset all states and redirect to login
      setShowResetPassword(false);
      setResetPasswordData({ resetToken: '', newPassword: '', confirmPassword: '' });
      setShowForgotPassword(false);
      
      // If we came from the email link, redirect to login page
      if (location.pathname === '/reset-password') {
        window.location.href = '/login';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset existing admin password for testing
  const resetAdminPassword = async () => {
    try {
      const response = await api.post(`${API_URL}/auth/forgot-password`, {
        email: 'admin@test.com'
      });
      
      if (response.data.reset_token) {
        await api.post(`${API_URL}/auth/reset-password`, {
          reset_token: response.data.reset_token,
          new_password: 'admin123'
        });
        toast.success('Admin password reset to: admin123');
      }
    } catch (error) {
      console.error('Failed to reset admin password:', error);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/salesqualifyd-logo.png" 
                alt="SALESQUALIFYD" 
                className="h-20 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Forgot Password</CardTitle>
            <CardDescription>
              Enter your email address and we&apos;ll send you a link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="Enter your email"
                  value={forgotPasswordData.email}
                  onChange={(e) => setForgotPasswordData({...forgotPasswordData, email: e.target.value})}
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-red-700 hover:bg-red-800"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full"
                onClick={() => setShowForgotPassword(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showResetPassword) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="/salesqualifyd-logo.png" 
                alt="SALESQUALIFYD" 
                className="h-20 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Set New Password</CardTitle>
            <CardDescription>
              Choose a strong password for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Token is hidden — auto-filled from URL */}
              <input type="hidden" value={resetPasswordData.resetToken} />
              
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={resetPasswordData.newPassword}
                  onChange={(e) => setResetPasswordData({...resetPasswordData, newPassword: e.target.value})}
                  required
                  minLength={6}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Re-enter your password"
                  value={resetPasswordData.confirmPassword}
                  onChange={(e) => setResetPasswordData({...resetPasswordData, confirmPassword: e.target.value})}
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-red-700 hover:bg-red-800"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Set Password & Continue'}
              </Button>
              
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full"
                onClick={() => {
                  if (location.pathname === '/reset-password') {
                    window.location.href = '/login';
                  } else {
                    setShowResetPassword(false);
                    setShowForgotPassword(false);
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row overflow-auto lg:overflow-hidden">
      {/* LEFT PANEL — 65% */}
      <div className="w-full lg:w-[65%] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-8 lg:p-12 relative overflow-hidden flex flex-col">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        
        <div className="relative z-10 flex flex-col flex-1 justify-center">
          <h1 className="text-4xl font-bold leading-tight mb-5">
            Every unicorn began with <span className="text-amber-500">a system.</span>
            <br />This is yours.
          </h1>
          
          <p className="text-base text-gray-300 leading-relaxed max-w-xl mb-3">
            Doctors don&apos;t wing surgeries. Pilots don&apos;t skip checklists.
            The businesses that dominate run on disciplined systems
            for finding, qualifying, and closing every opportunity.
          </p>
          
          <p className="text-base text-gray-400 leading-relaxed max-w-xl mb-8">
            Your complete sales & marketing operating system — from market research
            and GTM strategy to pipeline management and deal closure.
          </p>

          <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">What&apos;s inside</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-8">
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />AI MPO Engine & Playsheets</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />Sales & Marketing CRM</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />Email Sequences & DRIP</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />Social Media Automation</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />WhatsApp Business Suite</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />Web Funnels & Landing Pages</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />SEO & Competitive Intel</span>
            <span className="text-gray-300 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />Pipeline & Deal Tracking</span>
          </div>

          <p className="text-sm text-gray-400 mb-2">Don&apos;t have an account yet?</p>
          <a href="/pricing" className="inline-flex items-center bg-red-700 hover:bg-red-800 text-white font-semibold py-2.5 px-5 rounded-lg transition-all text-sm self-start">
            Sign Up — Plans from $5/mo
          </a>
          <p className="text-xs text-gray-600 mt-1.5">Cancel any time. 72-hour money-back guarantee.</p>
        </div>
      </div>

      {/* RIGHT PANEL — 35% */}
      <div className="w-full lg:w-[35%] flex items-center justify-center p-6 lg:p-8 bg-gray-50">
        <div className="w-full max-w-xs">
          <div className="text-center mb-6">
            <img src="/salesqualifyd-logo.png" alt="SALESQUALIFYD" className="h-10 w-auto mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">Welcome Back</h2>
            <p className="text-sm text-gray-500 mt-1">Sign in to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
              <Input id="email" type="email" placeholder="Enter your email" value={loginData.email} onChange={(e) => setLoginData({...loginData, email: e.target.value})} required className="h-9 text-sm" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <button type="button" className="text-xs text-red-800 hover:text-red-700 font-medium" onClick={() => setShowForgotPassword(true)}>Forgot?</button>
              </div>
              <Input id="password" type="password" placeholder="Enter your password" value={loginData.password} onChange={(e) => setLoginData({...loginData, password: e.target.value})} required className="h-9 text-sm" />
            </div>
            <Button type="submit" className="w-full h-9 bg-red-700 hover:bg-red-800 text-sm font-semibold" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
            <div className="text-center">
              <a href="/affiliate" className="text-xs text-red-800 hover:text-red-700 font-medium">Refer & Earn</a>
            </div>
          </form>

          <div className="flex flex-wrap justify-center gap-3 text-[10px] text-gray-400 mt-6">
            <a href="https://www.salesqualifyd.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Privacy</a>
            <a href="https://www.salesqualifyd.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Terms</a>
            <a href="https://www.salesqualifyd.com/refund" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Refund</a>
            <a href="https://www.salesqualifyd.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;